<template>
    <div class="table-wrapper">
        <div class="title-table">
            <h2>Tabla Viajes: {{ fecha_inicio }} a {{ fecha_fin }}</h2>
        </div>
        <template v-if="noDataMessage">
            <p class="no-data-message">{{ noDataMessage }}</p>
        </template>
        <template v-else>
            <div class="table-controls">
                <InputText v-model="searchQuery" placeholder="Buscar en Tabla..." class="p-mb-3 filter-input-table" />
            </div>

            <DataTable :value="filteredData" class="p-datatable-sm" v-model:expandedRows="expandedRows"
                :paginator="true" :rows="5" :sortField="sortField" :sortOrder="sortOrder"
                @update:sortField="updateSortField" @update:sortOrder="updateSortOrder" @rowExpand="onRowExpand"
                @rowCollapse="onRowCollapse">

                <Column expander style="width: 3rem"></Column>
                <Column field="total_kms" header="Total de KMS" sortable>
                    <template #body="slotProps">
                        {{ formattedNumber(slotProps.data.total_kms) }}
                    </template>
                </Column>
                <Column field="total_fact_sin_casetas" header="Total Fact Sin Casetas" sortable>
                    <template #body="slotProps">
                        {{ formattedMoney(slotProps.data.total_fact_sin_casetas) }}
                    </template>
                </Column>
                <Column field="fecha" header="Fecha" sortable>
                    <template #body="slotProps">
                        {{ formattedDate(slotProps.data.fecha_embarque) }}
                    </template>
                </Column>
                <Column field="carta_porte" header="Carta Porte" sortable>
                    <template #body="slotProps">
                        <span class="clickable" @click="downloadCartaPorte(slotProps.data.carta_porte)">
                            {{ slotProps.data.carta_porte }}
                        </span>
                    </template>
                </Column>
                <Column field="diesel" header="Diesel" sortable>
                    <template #body="slotProps">
                        {{ formattedMoney(slotProps.data.diesel) }}
                    </template>
                </Column>
                <Column field="factor_prom_variable" header="Factor Prom Variable" sortable>
                    <template #body="slotProps">
                        {{ formattedNumber(slotProps.data.factor_prom_variable) }}
                    </template>
                </Column>

                <template #expansion="slotProps">
                    <div class="expanded-content">
                        <p><strong>Nomenclatura:</strong> {{ slotProps.data.nomenclatura }}</p>
                        <p><strong>Casetas:</strong> {{ formattedMoney(slotProps.data.casetas) }}</p>
                        <p><strong>Casetas c/IVA:</strong> {{ formattedMoney(slotProps.data.casetas_iva) }}</p>
                        <p><strong>Total a Fact c/Casetas:</strong> {{
                            formattedMoney(slotProps.data.total_fact_con_casetas) }}</p>
                        <p><strong>Costo Variable:</strong> {{ formattedMoney(slotProps.data.costo_variable) }}</p>
                        <p><strong>Total Variable:</strong> {{ formattedMoney(slotProps.data.total_variable) }}</p>
                        <p><strong>Confirmación del Total a Facturar:</strong> {{
                            formattedNumber(slotProps.data.confirmacion_factura) }}
                        </p>
                        <p><strong>Diferencias:</strong> {{ formattedNumber(slotProps.data.diferencias) }}</p>
                        <p><strong>Prefactura:</strong> {{ slotProps.data.prefactura }}</p>
                    </div>
                </template>
            </DataTable>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps, defineExpose } from 'vue';
import axios from 'axios';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';

const tableFactData = ref([]);

const props = defineProps({
    fecha_inicio: String,
    fecha_fin: String
});

const expandedRows = ref([]);
const tableData = ref([]);
const noDataMessage = ref("");
const searchQuery = ref("");
const sortField = ref(null);
const sortOrder = ref(null);

const fetchTableData = async () => {
    try {
        const response = await fetch(`https://api-wmdata.fmce.com.mx/api/reports/km-mayor?fecha_inicio=${props.fecha_inicio}&fecha_fin=${props.fecha_fin}`);
        const data = await response.json();

        if (data.data?.message) {
            tableData.value = [];
            noDataMessage.value = data.data.message;
        } else {
            tableData.value = Array.isArray(data.data) ? data.data : [];
            noDataMessage.value = tableData.value.length ? "" : "No se encontraron datos en el rango de fechas";

            tableFactData.value = Array.isArray(data.data) ? data.data : [];
        }
    } catch (error) {
        console.error("Error al obtener datos de la tabla:", error);
        noDataMessage.value = "Error al cargar los datos.";
        tableData.value = [];
    }
};

const downloadCartaPorte = async (cartaPorte) => {
  try {
    const response = await axios.get(`https://api-wmdata.fmce.com.mx/api/descargar/${cartaPorte}`, {
      responseType: 'blob'
    });

    // Crear un enlace de descarga
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `CartaPorte_${cartaPorte}.xls`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error al descargar la Carta Porte:', error);
  }
};

const filteredData = computed(() => {
    if (!searchQuery.value) return tableData.value;
    const query = searchQuery.value.toLowerCase();
    return tableData.value.filter(row =>
        Object.values(row).some(value =>
            String(value).toLowerCase().includes(query)
        )
    );
});

const updateSortField = (field) => {
    sortField.value = field;
};

const updateSortOrder = (order) => {
    sortOrder.value = order;
};

const formattedMoney = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
};

const formattedNumber = (value) => {
    return new Intl.NumberFormat('es-MX').format(value);
};


const formattedDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
};


const onRowExpand = () => {
    // console.log("Fila expandida:", event.data);
};

const onRowCollapse = () => {
    // console.log("Fila colapsada:", event.data);

};

onMounted(fetchTableData);
const getData = () => tableFactData.value;
defineExpose({ getData, fetchTableData });
watch(() => [props.fecha_inicio, props.fecha_fin], fetchTableData);
</script>

<style scoped src="../styles/table.css"></style>
<style scoped>
.p-paginator-page-selected {
    background: #011f41 !important;
    color: #ffffff !important;
}

.clickable {
    /* color: blue; */
    cursor: pointer;
    text-decoration: underline;
}
</style>
